<template>
  <Teleport :to="teleportTarget" :disabled="!useTeleport">
    <Transition name="loading-fade">
      <div
        v-if="visible"
        class="loading-overlay"
        :class="[
          `loading-overlay--${variant}`,
          { 'loading-overlay--fullscreen': fullscreen }
        ]"
      >
        <div class="loading-overlay__content">
          <v-progress-circular
            indeterminate
            :color="spinnerColor"
            :size="spinnerSize"
            :width="3"
          />
          <p v-if="text" class="loading-overlay__text">{{ text }}</p>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, ref, watch, onUnmounted } from 'vue';

interface Props {
  show: boolean;
  text?: string;
  variant?: 'default' | 'save' | 'delete' | 'light';
  fullscreen?: boolean;
  spinnerSize?: number;
  /** 이 시간(ms) 이상 걸릴 때만 오버레이 표시 — 짧은 요청 깜빡임 방지 */
  delayMs?: number;
}

const props = withDefaults(defineProps<Props>(), {
  text: '',
  variant: 'default',
  fullscreen: false,
  spinnerSize: 40,
  delayMs: 220,
});

const visible = ref(false);
let delayTimer: ReturnType<typeof setTimeout> | null = null;

const clearDelayTimer = () => {
  if (delayTimer) {
    clearTimeout(delayTimer);
    delayTimer = null;
  }
};

watch(
  () => props.show,
  (isShowing) => {
    clearDelayTimer();
    if (isShowing) {
      if (props.delayMs <= 0) {
        visible.value = true;
        return;
      }
      delayTimer = setTimeout(() => {
        if (props.show) {
          visible.value = true;
        }
      }, props.delayMs);
    } else {
      visible.value = false;
    }
  },
  { immediate: true }
);

onUnmounted(clearDelayTimer);

const useTeleport = computed(() => props.fullscreen);
const teleportTarget = computed(() => (props.fullscreen ? 'body' : undefined));

const spinnerColor = computed(() => {
  switch (props.variant) {
    case 'save':
      return '#1565c0';
    case 'delete':
      return '#c62828';
    case 'light':
      return '#64748b';
    default:
      return '#1e3a5f';
  }
});
</script>

<style lang="scss">
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(244, 246, 249, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  backdrop-filter: blur(1px);

  &--save {
    background: rgba(244, 246, 249, 0.6);
  }

  &--delete {
    background: rgba(254, 242, 242, 0.55);
  }

  &--light {
    background: rgba(244, 246, 249, 0.4);
    backdrop-filter: none;
  }

  &--fullscreen {
    position: fixed;
    z-index: 9999;
    background: rgba(244, 246, 249, 0.72);
    backdrop-filter: blur(2px);
  }

  &__content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    padding: 8px 12px;
    background: transparent;
    border-radius: 8px;
    box-shadow: none;
  }

  &--fullscreen &__content {
    padding: 24px 32px;
    background: #fff;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  }

  &__text {
    font-size: 13px;
    font-weight: 500;
    color: #475569;
    margin: 0;
    white-space: nowrap;
  }
}

.loading-fade-enter-active {
  transition: opacity 0.15s ease;
}

.loading-fade-leave-active {
  transition: opacity 0.1s ease;
}

.loading-fade-enter-from,
.loading-fade-leave-to {
  opacity: 0;
}
</style>
